import { alpha, Box, Grid, Paper, useMantineColorScheme } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { getPaginatedData, normalizeString } from "common/utils";
import { PaginationBlock } from "components/products/PaginationBlock";
import ProductCards from "components/products/ProductCards";
import { ProductFilterSidebar } from "components/products/ProductFilterSidebar";
import { useEffect, useMemo } from "react";
import { useProductStore } from "stores/useProductStore";
import { COLORS } from "styles/colors";

const ProductsPage = () => {
  const isSmallScreen = useMediaQuery("(max-width: 768px)");
  const pageSize = 30;

  const allProducts = useProductStore((s) => s.allProducts);
  const brands = useProductStore((s) => s.brands);
  const categories = useProductStore((s) => s.categories);

  const activePage = useProductStore((s) => s.activePage);
  const setActivePage = useProductStore((s) => s.setActivePage);
  const selectedBrand = useProductStore((s) => s.selectedBrand);
  const setSelectedBrand = useProductStore((s) => s.setSelectedBrand);
  const selectedCategory = useProductStore((s) => s.selectedCategory);
  const setSelectedCategory = useProductStore((s) => s.setSelectedCategory);
  const topSelling = useMemo(() => {
    return useProductStore.getState().getTopSelling();
  }, []);

  const filteredProducts = allProducts.items.filter((p) => {
    const selectedBrandId = brands.find(
      (b) => normalizeString(b.name) === normalizeString(selectedBrand),
    )?.id;
    const selectedCategoryId = categories.find(
      (c) => normalizeString(c.name) === normalizeString(selectedCategory),
    )?.id;

    const brandIds =
      p.brandIds
        ?.split(",")
        .map((id) => id.trim().toLowerCase())
        .filter(Boolean) ?? [];
    const categoryIds =
      p.categoryIds
        ?.split(",")
        .map((id) => id.trim().toLowerCase())
        .filter(Boolean) ?? [];

    const brandMatch = selectedBrandId
      ? brandIds.includes(selectedBrandId.toString().trim().toLowerCase())
      : true;

    const categoryMatch = selectedCategoryId
      ? categoryIds.includes(selectedCategoryId.toString().trim().toLowerCase())
      : true;

    return categoryMatch && brandMatch;
  });

  const paginatedProducts = getPaginatedData(
    filteredProducts,
    activePage,
    pageSize,
  );
  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === "dark";

  // run and monitor filteredProducts.length, activePage, pageSize for changes...if changed, run again
  useEffect(() => {
    if (!useProductStore.persist.hasHydrated()) return; // TODO : may not need this line

    const maxPage = Math.ceil(filteredProducts.length / pageSize);
    if (activePage > maxPage && maxPage > 0) {
      setActivePage(maxPage);
    }
  }, [filteredProducts.length, activePage, pageSize]);

  // run only once
  useEffect(() => {
    if (!useProductStore.persist.hasHydrated()) return; // TODO : may not need this line

    useProductStore.getState().fetchBrands();
    useProductStore.getState().fetchCategories();
  }, []);

  return (
    <Box>
      <Grid gutter="md">
        {!isSmallScreen && (
          <Grid.Col span={{ base: 12, md: 3 }}>
            <ProductFilterSidebar
              categories={categories}
              brands={brands}
              selectedBrand={selectedBrand}
              selectedCategory={selectedCategory}
              onSelectBrand={setSelectedBrand}
              onSelectCategory={setSelectedCategory}
            />
          </Grid.Col>
        )}
        <Grid.Col span={{ base: 12, md: 9 }}>
          <PaginationBlock
            activePage={activePage}
            total={Math.ceil(filteredProducts.length / pageSize)}
            onPageChange={setActivePage}
            showClear={!!selectedBrand || !!selectedCategory}
            onClearFilters={() => {
              setSelectedBrand(undefined);
              setSelectedCategory(undefined);
            }}
          />
          <br />

          {paginatedProducts && paginatedProducts.length === 0 ? (
            <Paper p="md" withBorder={false} style={{ position: "relative" }}>
              No products available for chosen category and/or brand. Please
              clear the filter to reload.
            </Paper>
          ) : (
            <>
              <ProductCards
                products={paginatedProducts}
                title="products"
                mainText="Browse our huge range of"
                span={{ base: 12, md: 4, sm: 4 }}
              />
            </>
          )}
          <PaginationBlock
            activePage={activePage}
            total={Math.ceil(filteredProducts.length / pageSize)}
            onPageChange={setActivePage}
          />
        </Grid.Col>
      </Grid>
      <Box
        mt="xl"
        py="lg"
        px="md"
        style={{
          backgroundColor: isDark
            ? "transparent"
            : alpha(COLORS.colorDarkMode, 0.1),
        }}
      >
        <ProductCards products={topSelling} title="top sellers" />
      </Box>
    </Box>
  );
};
export default ProductsPage;
